import React, { useMemo, useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Tooltip,
  Chip,
  IconButton,
  Stack,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Popover,
  CircularProgress,
  Autocomplete,
  Avatar,
} from "@mui/material";
import {
  Print as PrintIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  CalendarToday,
  Phone,
} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  billList,
  billSettlement,
  getFreightsByBilPartyIdApi,
  billUpdate,
  billDelete,
  partyList,
} from "../../../Services/Services";
import { printBillReport, downloadBillReport } from "./billPDFGenerator";
import { T } from "../../../theme";
import { useDebounce } from "../../../Hooks/useDebounce";
import { useNavigate } from "react-router-dom";

// ── Shared Styles ──────────────────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const successBtn = {
  ...sq,
  border: `1.5px solid ${T.successBorder}`,
  bgcolor: T.successBg,
  color: T.success,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#d1fae5", borderColor: T.success, boxShadow: "none" },
};
const warningBtn = {
  ...sq,
  border: `1.5px solid ${T.warningBorder}`,
  bgcolor: T.warningBg,
  color: T.warning,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fef3c7", borderColor: T.warning, boxShadow: "none" },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.white },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

const getInitials = (s) =>
  s
    ?.split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) ?? "";

const getRefNo = (row) =>
  `PRL/${row.billing_id}/${getInitials(row.party_name)}`;

const getLrPreview = (freights = []) => {
  if (!Array.isArray(freights) || freights.length === 0) return "—";

  const firstLr = freights[0]?.lr_number || `#${freights[0]?.freight_id}`;
  const extraCount = freights.length - 1;

  return extraCount > 0 ? `${firstLr} +${extraCount}` : firstLr;
};

const LRTruckTooltipContent = ({ freights = [] }) => {
  if (!Array.isArray(freights) || freights.length === 0) {
    return (
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 600 }}>
        No LR found
      </Typography>
    );
  }

  return (
    <Box sx={{ minWidth: 130, maxWidth: 280 }}>
      <TableContainer
        sx={{
          borderRadius: "4px",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        <Table
          size="small"
          sx={{
            borderCollapse: "collapse",
            "& td, & th": {
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              px: 0.8,
              py: 0.45,
              lineHeight: 1.15,
              whiteSpace: "nowrap",
            },
            "& th": {
              bgcolor: "rgba(255,255,255,0.10)",
              color: "rgba(255,255,255,0.78)",
              fontSize: "0.55rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            },
            "& td": {
              fontSize: "0.62rem",
              color: "#fff",
            },
            "& tr:last-child td": {
              borderBottom: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>LR No</TableCell>
              <TableCell>Truck No</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {freights.map((f, idx) => (
              <TableRow
                key={f.freight_id}
                sx={{
                  bgcolor:
                    idx % 2 === 0
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(255,255,255,0.03)",
                }}
              >
                <TableCell
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 800,
                    color: "#fff",
                  }}
                >
                  {f.lr_number || `#${f.freight_id}`}
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.92)",
                    maxWidth: 130,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {f.truck_no || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ── Dialog Header ──────────────────────────────────────────────────
const DialogHeader = ({ title, onClose }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        color: T.white,
        fontSize: "0.88rem",
        letterSpacing: "0.01em",
      }}
    >
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{
        color: "rgba(255,255,255,0.8)",
        p: 0.5,
        ...sq,
        "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
      }}
    >
      <CloseIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Box>
);

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete,
  isSuccess,
  isWarning,
  isEdit,
  isView,
  onClick,
}) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : isSuccess
          ? T.success
          : isWarning
            ? T.warning
            : T.primary;

  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : isSuccess
          ? T.successBg
          : isWarning
            ? T.warningBg
            : T.primaryBg;

  const bd = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : isSuccess
          ? T.successBorder
          : isWarning
            ? T.warningBorder
            : T.border;

  const hBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : isSuccess
          ? "#d1fae5"
          : isWarning
            ? "#fef3c7"
            : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          color: c,
          bgcolor: bg,
          border: `1px solid ${bd}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hBg,
            borderColor: c,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.25, sm: 1.5 },
      ...sq,
      position: "relative",
      overflow: "hidden",
      bgcolor: `${color}12`,
      border: `1px solid ${color}40`,
      boxShadow: "none",
      minWidth: 0,
      transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 4px 15px ${color}25`,
        borderColor: color,
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        bottom: -15,
        right: -10,
        width: 50,
        height: 50,
        borderRadius: "50%",
        bgcolor: `${color}15`,
        zIndex: 0,
      }}
    />
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Typography
        sx={{
          fontSize: { xs: "0.6rem", sm: "0.65rem" },
          color: T.textSub,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          mb: 0.5,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "0.9rem", sm: "1.1rem" },
          fontWeight: 800,
          color: T.text,
          lineHeight: 1.1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Paper>
);
// ── DeletePopover ──────────────────────────────────────────────────
const ConfirmPopover = ({
  anchorEl,
  onClose,
  onConfirm,
  title,
  message,
  isSettle,
}) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 240 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: isSettle ? T.successBg : T.redBg,
            border: `1.5px solid ${isSettle ? T.successBorder : T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSettle ? (
            <CheckCircle sx={{ fontSize: 20, color: T.success }} />
          ) : (
            <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
          )}
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        {message}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{
            ...(isSettle ? successBtn : dangerBtn),
            height: 30,
            fontSize: "0.7rem",
          }}
        >
          {isSettle ? "Settle" : "Delete"}
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── MobileBillCard ─────────────────────────────────────────────────
const MobileBillCard = ({
  row,
  idx,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onPrint,
  onSettle,
  onExport,
}) => {
  const isSettled = row.is_complete;
  const isNegative = row.rem_amount < 0;
  const statusColor = isSettled ? T.success : isNegative ? T.red : T.warning;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Top Row */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <ReceiptIcon
            sx={{ fontSize: 20, color: expanded ? T.white : T.primary }}
          />
          <Typography
            sx={{
              fontSize: "0.52rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.78rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getRefNo(row)}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textSub,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 0.25,
            }}
          >
            {row.party_name || "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <CalendarToday sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography sx={{ fontSize: "0.6rem", color: T.textMuted }}>
              {formatDate(row.created_at)}
            </Typography>
          </Box>
          <Chip
            label={isSettled ? "Settled" : isNegative ? "Overpaid" : "Pending"}
            size="small"
            sx={{
              mt: 0.4,
              width: "fit-content",
              ...sq,
              bgcolor: `${statusColor}14`,
              color: statusColor,
              fontSize: "0.55rem",
              height: 16,
              border: `1px solid ${statusColor}30`,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.55rem", color: T.textMuted, fontWeight: 600 }}
            >
              Balance
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontSize: "0.8rem", color: statusColor }}
            >
              {formatCurrency(row.rem_amount)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 26,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        </Box>
      </Box>

      {/* Expanded */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.white,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1.25}>
            {/* Amount Summary */}
            <Box
              sx={{
                p: 1,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.75,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💰 Amount Summary
              </Typography>
              <Grid container spacing={0.5}>
                {[
                  ["Total", formatCurrency(row.total_amount), T.text],
                  ["TDS", formatCurrency(row.tds_amount), "#DC2626"],
                  [
                    "Deduction",
                    formatCurrency(row.deduction_amount),
                    "#EA580C",
                  ],
                  ["Net Payable", formatCurrency(row.net_payable), T.primary],
                  ["Paid", formatCurrency(row.paid_amount), T.success],
                  ["Balance", formatCurrency(row.rem_amount), statusColor],
                ].map(([l, v, c]) => (
                  <Grid item xs={4} key={l}>
                    <Typography
                      sx={{
                        fontSize: "0.58rem",
                        color: T.textMuted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                    >
                      {l}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: 700, color: c }}
                    >
                      {v}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Contact & LR */}
            <Stack spacing={0.5}>
              {row.contact_no && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      ...sq,
                      bgcolor: T.white,
                      border: `1px solid ${T.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Phone sx={{ fontSize: 12, color: T.primary }} />
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.72rem", color: T.text, fontWeight: 500 }}
                  >
                    {row.contact_no}
                  </Typography>
                </Box>
              )}
              {row.freights?.length > 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      color: T.primary,
                      mb: 0.5,
                    }}
                  >
                    🚛 LR ({row.freights.length})
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {row.freights.map((f, i) => (
                      <Chip
                        key={i}
                        label={`#${f.freight_id}`}
                        size="small"
                        sx={{
                          ...sq,
                          bgcolor: T.white,
                          color: T.primary,
                          border: `1px solid ${T.border}`,
                          fontSize: "0.55rem",
                          height: 18,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              {row.follow_date && (
                <Typography sx={{ fontSize: "0.65rem", color: T.textSub }}>
                  <strong>Follow Date:</strong> {formatDate(row.follow_date)}
                </Typography>
              )}
              {row.remarks && (
                <Typography sx={{ fontSize: "0.65rem", color: T.text }}>
                  <strong>Remark:</strong> {row.remarks}
                </Typography>
              )}
              {row.deduction_remarks && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.6,
                    bgcolor: "#FFF7ED",
                    border: "1px solid #FED7AA",
                    ...sq,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      color: "#C2410C",
                      fontWeight: 700,
                    }}
                  >
                    Deduction Reason:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: "#9A3412",
                      fontWeight: 600,
                    }}
                  >
                    {row.deduction_remarks}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {!isSettled && (
              <Button
                size="small"
                startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
                onClick={(e) => onSettle(e, row.billing_id)}
                sx={{ ...successBtn, height: 30, px: 1.5 }}
              >
                Settle
              </Button>
            )}
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
              onClick={() => onExport(row.freights)}
              sx={{ ...successBtn, height: 30, px: 1.5 }}
            >
              Export
            </Button>
            <Button
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
              onClick={() => onPrint(row)}
              sx={{ ...warningBtn, height: 30, px: 1.5 }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => onDelete(e, row.billing_id)}
              sx={{ ...dangerBtn, height: 30, px: 1.5 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ── Pagination Sx ──────────────────────────────────────────────────
const paginationSx = {
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  mt: 1,
  ...sq,
  "& .MuiTablePagination-toolbar": {
    minHeight: 44,
    flexWrap: "wrap",
    gap: 0.5,
    px: 1,
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.68rem",
    margin: 0,
    color: T.textSub,
    fontWeight: 500,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.68rem",
    ...sq,
    border: `1px solid ${T.border}`,
    bgcolor: T.white,
    px: "6px",
  },
  "& .MuiTablePagination-actions button": { color: T.primary },
};

// ── Main Component ─────────────────────────────────────────────────
export default function BillSummaryTable() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [billData, setBillData] = useState([]);
  const [settleAnchorEl, setSettleAnchorEl] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [settleId, setSettleId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [freightOptions, setFreightOptions] = useState([]);
  const [totalNetPayable, setTotalNetPayable] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [partyOptions, setParties] = useState([]);
  const [expandedCardId, setExpandedCardId] = useState(null);

  // ── Filter state (raw — user typing) ──
  const [filters, setFilters] = useState({
    lrNumber: "",
    billDate: "",
    party: "",
  });

  // ── Debounced filters (sent to API after 500ms) ──
  const debouncedLrNumber = useDebounce(filters.lrNumber, 500);
  const debouncedBillDate = useDebounce(filters.billDate, 500);
  const debouncedParty = useDebounce(filters.party, 500);

  // ── Helpers ──
  const handleFilterChange = (key, value) =>
    setFilters((p) => ({ ...p, [key]: value }));

  const clearFilters = () =>
    setFilters({ lrNumber: "", billDate: "", party: "" });

  const hasActiveFilters =
    filters.lrNumber || filters.billDate || filters.party;

  // ── Fetch parties on mount ──
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setParties(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("Failed to fetch parties:", err);
        setParties([]);
      }
    };
    fetchParties();
  }, []);

  // ── Core fetch function ──
  const fetchFilteredBills = async (pageNo = 1, appliedFilters) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await billList(pageNo, appliedFilters);
      const bills = Array.isArray(res.results) ? res.results : [];
      if (pageNo === 1) setBillData(bills);
      else setBillData((p) => [...p, ...bills]);
      setHasMore(!!res.next);
      setPage(pageNo + 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const appliedFilters = {
      lrNumber: debouncedLrNumber,
      billDate: debouncedBillDate,
      party: debouncedParty,
    };
    setBillData([]);
    setPage(1);
    setExpandedCardId(null);
    fetchFilteredBills(1, appliedFilters);
  }, [debouncedLrNumber, debouncedBillDate, debouncedParty]);

  // ── Infinite scroll ──
  const fetchMoreData = () => {
    if (!loading && hasMore) {
      fetchFilteredBills(page, {
        lrNumber: debouncedLrNumber,
        billDate: debouncedBillDate,
        party: debouncedParty,
      });
    }
  };

  const totals = useMemo(
    () => ({
      total_amount: billData.reduce(
        (s, r) => s + Number(r.total_amount || 0),
        0,
      ),
      tds_amount: billData.reduce((s, r) => s + Number(r.tds_amount || 0), 0),
      deduction_amount: billData.reduce(
        (s, r) => s + Number(r.deduction_amount || 0),
        0,
      ),
      net_payable: billData.reduce((s, r) => s + Number(r.net_payable || 0), 0),
      paid_amount: billData.reduce((s, r) => s + Number(r.paid_amount || 0), 0),
      rem_amount: billData.reduce((s, r) => s + Number(r.rem_amount || 0), 0),
    }),
    [billData],
  );

  const filteredData = billData;

  // ── Edit ──
  const handleEditClick = async (row) => {
    try {
      const res = await getFreightsByBilPartyIdApi(row.party_id);

      const billedFreights = row.freights || [];
      const billedIds = billedFreights.map((f) => f.freight_id);

      const freightMap = new Map();

      billedFreights.forEach((f) => {
        freightMap.set(f.freight_id, f);
      });

      (res || []).forEach((f) => {
        if (!freightMap.has(f.freight_id)) {
          freightMap.set(f.freight_id, f);
        }
      });

      const allFreights = Array.from(freightMap.values());

      navigate("/dashboard/bill-creation", {
        state: {
          isEditMode: true,
          billingData: {
            billing_id: row.billing_id,
            party_id: row.party_id,
            party_name: row.party_name,
            total_amount: row.total_amount,
            paid_amount: row.paid_amount,
            rem_amount: row.rem_amount,
            halting_charge: row.halting_charge || 0,
            halting_message: row.halting_message || "",
            remarks: row.remarks || "",
            freight_amount: row.freight_amount || 0,
            branch_id: row.branch_id || null,
            tds_percent: row.tds_percent || 0,
            tds_amount: row.tds_amount || 0,
            deduction_amount: row.deduction_amount || 0,
            deduction_remarks: row.deduction_remarks || "",
          },
          freights: allFreights,
          selectedFreightIds: billedIds,
        },
      });
    } catch (err) {
      console.error("Edit navigation failed:", err);
    }
  };

  // ── Settle ──
  const handleSettle = async (id) => {
    const d = await billSettlement(id);
    setBillData(d.results);
    setSettleAnchorEl(null);
    setSettleId(null);
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    try {
      const r = await billDelete(id);
      setBillData(r);
    } catch (e) {
      console.error(e);
    }
    setDeleteAnchorEl(null);
    setDeleteId(null);
  };

  // ── IsBill toggle in edit modal ──
  const handleIsBillToggle = (freightId) => {
    setFreightOptions((prev) => {
      const updated = prev.map((r) =>
        r.freight_id === freightId ? { ...r, is_bill: !r.is_bill } : r,
      );
      setTotalNetPayable(
        updated.reduce(
          (s, r) => s + (r.is_bill ? parseFloat(r.net_amount) || 0 : 0),
          0,
        ),
      );
      return updated;
    });
  };

  // ── Submit edit ──
  const handleSubmit = async () => {
    const billedIds = freightOptions
      .filter((r) => r.is_bill)
      .map((r) => r.freight_id);
    const data = {
      billing_id: selectedRow.billing_id,
      freight_id: billedIds,
      freight_data: freightOptions,
      total_amount: totalNetPayable,
      rem_amount: Number(totalNetPayable) - Number(selectedRow.paid_amount),
    };
    const res = await billUpdate(selectedRow.billing_id, data);
    if (res) {
      setBillData(res);
      setOpenEditModal(false);
      setFreightOptions([]);
      setSelectedRow(null);
      setTotalNetPayable(0);
    }
  };

  // ── Print / Export ──
  const handlePrint = (row) =>
    printBillReport([row], {
      total_amount: row.total_amount,
      paid_amount: row.paid_amount,
      rem_amount: row.rem_amount,
    });

  const handlePrintAll = () => {
    if (!billData.length) return alert("No bills to print");
    printBillReport(billData, totals);
  };

  const handleDownloadAll = () => {
    if (!billData.length) return alert("No bills to download");
    downloadBillReport(billData, totals);
  };

  const handleExportExcel = async (freightData) => {
    const XLSX = await import("xlsx-js-style");
    const exp = freightData.map((r, i) => ({
      "S.No": i + 1,
      "LR ID": r.freight_id,
      "LR Number": r.lr_number || "—",
      "Truck No": r.truck_no,
      "Party Name": r.party_name,
      "Loading Date": formatDate(r.loading_date),
      "Unloading Date": formatDate(r.unloading_date),
      From: r.from_location,
      To: r.to_location,
      "Net Payable": r.net_payable,
    }));
    const ws = XLSX.utils.json_to_sheet(exp);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Freight_List");
    XLSX.writeFile(
      wb,
      `Freight_List_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  // ─────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stats ── */}
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        {[
          {
            title: "Total Amount",
            value: formatCurrency(totals.total_amount),
            color: "#7c3aed",
          },
          {
            title: "TDS Deducted",
            value: formatCurrency(totals.tds_amount),
            color: "#DC2626",
          },
          {
            title: "Deductions",
            value: formatCurrency(totals.deduction_amount),
            color: "#EA580C",
          },
          {
            title: "Net Payable",
            value: formatCurrency(totals.net_payable),
            color: T.primary,
          },
          {
            title: "Paid Amount",
            value: formatCurrency(totals.paid_amount),
            color: T.success,
          },
          {
            title: "Remaining",
            value: formatCurrency(totals.rem_amount),
            color: T.red,
          },
        ].map(({ title, value, color }) => (
          <Grid item xs={6} sm={4} md={2} key={title}>
            <StatCard title={title} value={value} color={color} />
          </Grid>
        ))}
      </Grid>

      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* LR No — debounced via useDebounce */}
          <Grid item xs={12} sm={6} md={2.6}>
            <TextField
              size="small"
              placeholder="LR No"
              label="LR Number"
              fullWidth
              value={filters.lrNumber}
              onChange={(e) => handleFilterChange("lrNumber", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Bill Date — debounced via useDebounce */}
          <Grid item xs={12} sm={6} md={2.6}>
            <TextField
              type="date"
              size="small"
              label="Bill Date"
              fullWidth
              value={filters.billDate}
              onChange={(e) => handleFilterChange("billDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
                inputProps: { max: new Date().toISOString().split("T")[0] },
              }}
              sx={{
                ...filterInputSx,
                "& input[type='date']::-webkit-calendar-picker-indicator": {
                  opacity: 0,
                  position: "absolute",
                  width: "100%",
                  cursor: "pointer",
                },
              }}
            />
          </Grid>

          {/* Party — debounced via useDebounce */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={partyOptions}
              getOptionLabel={(o) => o?.name || ""}
              value={partyOptions.find((o) => o.name === filters.party) || null}
              onChange={(_, v) => handleFilterChange("party", v?.name || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Party Name"
                  size="small"
                  label="Party Name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Print */}
          <Grid item xs={6} sm="auto">
            <Button
              size="small"
              onClick={handlePrintAll}
              startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
              sx={{ ...warningBtn, height: 34, px: 2, minWidth: 96 }}
            >
              Print
            </Button>
          </Grid>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Grid item xs="auto">
              <Button
                size="small"
                onClick={clearFilters}
                sx={{ ...dangerBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </Button>
            </Grid>
          )}

          {/* Bill count */}
          <Grid item xs={12} sm="auto">
            <Box
              sx={{
                height: 34,
                px: 1.75,
                minWidth: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: T.white,
                border: `1.5px solid ${T.border}`,
              }}
            >
              <ReceiptIcon sx={{ fontSize: 15, color: T.primary }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: T.primary,
                  lineHeight: 1,
                }}
              >
                {billData.length} Bills
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Table / Mobile ── */}
      {isMobile ? (
        loading && billData.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : filteredData.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <ReceiptIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              No bills found
            </Typography>
          </Box>
        ) : (
          <Box
            id="bill-mobile-container"
            sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={billData.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="bill-mobile-container"
            >
              {filteredData.map((row, idx) => (
                <MobileBillCard
                  key={row.billing_id}
                  row={row}
                  idx={idx}
                  expanded={expandedCardId === row.billing_id}
                  onToggle={() => handleCardToggle(row.billing_id)}
                  onEdit={handleEditClick}
                  onDelete={(e, id) => {
                    setDeleteAnchorEl(e.currentTarget);
                    setDeleteId(id);
                  }}
                  onPrint={handlePrint}
                  onSettle={(e, id) => {
                    setSettleAnchorEl(e.currentTarget);
                    setSettleId(id);
                  }}
                  onExport={handleExportExcel}
                />
              ))}
            </InfiniteScroll>
          </Box>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            height: "calc(100vh - 220px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            id="bill-summary-container"
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={billData.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="bill-summary-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1420,
                  borderCollapse: "collapse",
                  "& td, & th": {
                    py: 0,
                    px: 1,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    height: 28,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                  },
                  "& td": {
                    height: 26,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { l: "Settle", sticky: true, left: 0, w: 50 },
                      { l: "#", sticky: true, left: 50, w: 35 },
                      { l: "LR No(s)", sticky: true, left: 85, w: 140 },
                      { l: "Ref No", sticky: true, left: 225, w: 150 }, // ← moved here, left = 85+140
                      { l: "Party Name", w: 160 },
                      { l: "Bill Date", w: 100 },
                      { l: "Total Amt", w: 100 },
                      { l: "TDS %", w: 60 },
                      { l: "TDS Amt", w: 90 },
                      { l: "Deduction", w: 90 },
                      { l: "Net Payable", w: 100 },
                      { l: "Paid Amt", w: 100 },
                      { l: "Balance", w: 100 },
                      { l: "Contact", w: 100 },
                      { l: "GST", w: 120 },
                      { l: "Follow Date", w: 100 },
                      { l: "Remarks", w: 150 },
                      { l: "Actions", sticky: true, right: 0, w: 130 },
                    ].map(({ l, sticky, left, right, w }) => (
                      <TableCell
                        key={l}
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                          ...(sticky && {
                            position: "sticky",
                            ...(left !== undefined && { left }),
                            ...(right !== undefined && { right }),
                            zIndex: 12,
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && billData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={19}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={19}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <ReceiptIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((row, idx) => {
                      const isSettled = row.is_complete;
                      const isNegative = row.rem_amount < 0;

                      // Keep original semantic row coloring but update alt row colors
                      const rowBg = isSettled
                        ? "#e8f8ee"
                        : isNegative
                          ? "#fde8e8"
                          : idx % 2 === 0
                            ? T.baseRow
                            : T.altRow;

                      const statusColor = isSettled
                        ? T.success
                        : isNegative
                          ? T.red
                          : T.warning;
                      const stickyCell = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      };

                      return (
                        <TableRow
                          key={row.billing_id}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* Settle checkbox — unchanged */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              ...stickyCell,
                              p: "2px 6px",
                            }}
                          >
                            <Checkbox
                              size="small"
                              disabled={row.is_complete}
                              onClick={(e) => {
                                setSettleAnchorEl(e.currentTarget);
                                setSettleId(row.billing_id);
                              }}
                              sx={{
                                p: 0,
                                color: T.primary,
                                "&.Mui-checked": { color: T.primary },
                              }}
                            />
                          </TableCell>

                          {/* # — unchanged left:50 */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 50,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>

                          {/* LR No — NOW sticky at left: 85 */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 85,
                              ...stickyCell,
                            }}
                          >
                            <Tooltip
                              arrow
                              placement="top"
                              disableHoverListener={!row.freights?.length}
                              title={
                                <LRTruckTooltipContent
                                  freights={row.freights}
                                />
                              }
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  maxWidth: 130,
                                  cursor: row.freights?.length
                                    ? "pointer"
                                    : "default",
                                }}
                              >
                                <Chip
                                  label={getLrPreview(row.freights)}
                                  size="small"
                                  sx={{
                                    ...sq,
                                    height: 16,
                                    maxWidth: 110,
                                    bgcolor: T.white,
                                    color: T.primary,
                                    border: `1px solid ${T.border}`,
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    "& .MuiChip-label": {
                                      px: 0.75,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    },
                                  }}
                                />
                                {row.freights?.length > 1 && (
                                  <Avatar
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      fontSize: "0.55rem",
                                      fontWeight: 800,
                                      bgcolor: T.primary,
                                      color: T.white,
                                    }}
                                  >
                                    {row.freights.length}
                                  </Avatar>
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>

                          {/* Ref No — NOW sticky at left: 225 (85 + 140) */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 225,
                              ...stickyCell,
                            }}
                          >
                            <Chip
                              label={getRefNo(row)}
                              size="small"
                              sx={{
                                ...sq,
                                bgcolor: T.white,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontSize: "0.6rem",
                                height: 16,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          {/* Party Name — no LR No cell here anymore */}
                          <TableCell>
                            <Tooltip title={row.party_name || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.party_name || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* Bill Date */}
                          <TableCell>
                            <Chip
                              label={formatDate(row.created_at)}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 16,
                                ...sq,
                                bgcolor: T.white,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>
                          {/* Total Amt */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: T.text,
                              }}
                            >
                              {formatCurrency(row.total_amount)}
                            </Typography>
                          </TableCell>

                          {/* TDS % */}
                          <TableCell align="center">
                            {row.tds_percent > 0 ? (
                              <Chip
                                label={`${row.tds_percent}%`}
                                size="small"
                                sx={{
                                  ...sq,
                                  height: 16,
                                  bgcolor: "#FEE2E2",
                                  color: "#DC2626",
                                  border: "1px solid #FECACA",
                                  fontSize: "0.58rem",
                                  fontWeight: 700,
                                  "& .MuiChip-label": { px: 0.6 },
                                }}
                              />
                            ) : (
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.textMuted }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* TDS Amt */}
                          <TableCell align="right">
                            {row.tds_amount > 0 ? (
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  color: "#DC2626",
                                }}
                              >
                                {formatCurrency(row.tds_amount)}
                              </Typography>
                            ) : (
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.textMuted }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* Deduction */}
                          <TableCell align="right">
                            {row.deduction_amount > 0 ? (
                              <Tooltip
                                title={row.deduction_remarks || ""}
                                arrow
                              >
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    color: "#EA580C",
                                  }}
                                >
                                  {formatCurrency(row.deduction_amount)}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography
                                sx={{ fontSize: "0.72rem", color: T.textMuted }}
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* Net Payable */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                color: T.primary,
                              }}
                            >
                              {formatCurrency(row.net_payable)}
                            </Typography>
                          </TableCell>

                          {/* Paid Amt */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: T.success,
                              }}
                            >
                              {formatCurrency(row.paid_amount)}
                            </Typography>
                          </TableCell>

                          {/* Balance */}
                          <TableCell align="right">
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: statusColor,
                              }}
                            >
                              {formatCurrency(row.rem_amount)}
                            </Typography>
                          </TableCell>

                          {/* Contact */}
                          <TableCell>
                            <Typography sx={{ fontSize: "0.75rem" }}>
                              {row.contact_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* GST */}
                          <TableCell>
                            <Typography sx={{ fontSize: "0.75rem" }}>
                              {row.gst_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* Follow Date */}
                          <TableCell>
                            <Typography sx={{ fontSize: "0.75rem" }}>
                              {row.follow_date
                                ? formatDate(row.follow_date)
                                : "—"}
                            </Typography>
                          </TableCell>

                          {/* Remarks */}
                          <TableCell>
                            <Tooltip title={row.remarks || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  maxWidth: 150,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.remarks || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* Actions */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              ...stickyCell,
                              py: "2px !important",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                isEdit
                                onClick={() => handleEditClick(row)}
                              />
                              <ActionButton
                                tip="Print"
                                icon={<PrintIcon />}
                                isWarning
                                onClick={() => handlePrint(row)}
                              />
                              <ActionButton
                                tip="Export"
                                icon={<DownloadIcon />}
                                isSuccess
                                onClick={() => handleExportExcel(row.freights)}
                              />
                              <ActionButton
                                tip="Delete"
                                icon={<DeleteIcon />}
                                isDelete
                                onClick={(e) => {
                                  setDeleteAnchorEl(e.currentTarget);
                                  setDeleteId(row.billing_id);
                                }}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}

                  {/* Totals Row */}
                  {filteredData.length > 0 && (
                    <TableRow
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 20,
                        "& td": {
                          bgcolor: `${T.successBg} !important`,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                          py: 1,
                          borderTop: `2px solid ${T.primary}`,
                          borderBottom: "none",
                        },
                      }}
                    >
                      {/* TOTAL label — spans Settle + # + LR No (3 cols), sticky left:0 */}
                      <TableCell
                        colSpan={3}
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 22,
                          bgcolor: `${T.successBg} !important`,
                          color: T.primary,
                          fontWeight: 800,
                        }}
                      >
                        TOTAL
                      </TableCell>

                      {/* Ref No — sticky left:225, empty */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 225,
                          zIndex: 22,
                          bgcolor: `${T.successBg} !important`,
                        }}
                      />

                      {/* Party Name */}
                      <TableCell />

                      {/* Bill Date */}
                      <TableCell />

                      {/* Total Amt */}
                      <TableCell
                        align="right"
                        sx={{ color: T.primary, fontWeight: 700 }}
                      >
                        {formatCurrency(totals.total_amount)}
                      </TableCell>

                      {/* TDS % */}
                      <TableCell
                        sx={{ color: T.textMuted, fontSize: "0.68rem" }}
                      >
                        —
                      </TableCell>

                      {/* TDS Amt */}
                      <TableCell
                        align="right"
                        sx={{ color: "#DC2626", fontWeight: 700 }}
                      >
                        {formatCurrency(totals.tds_amount)}
                      </TableCell>

                      {/* Deduction */}
                      <TableCell
                        align="right"
                        sx={{ color: "#EA580C", fontWeight: 700 }}
                      >
                        {formatCurrency(totals.deduction_amount)}
                      </TableCell>

                      {/* Net Payable */}
                      <TableCell
                        align="right"
                        sx={{ color: T.primary, fontWeight: 800 }}
                      >
                        {formatCurrency(totals.net_payable)}
                      </TableCell>

                      {/* Paid */}
                      <TableCell
                        align="right"
                        sx={{ color: T.success, fontWeight: 700 }}
                      >
                        {formatCurrency(totals.paid_amount)}
                      </TableCell>

                      {/* Balance */}
                      <TableCell
                        align="right"
                        sx={{ color: T.red, fontWeight: 800 }}
                      >
                        {formatCurrency(totals.rem_amount)}
                      </TableCell>

                      {/* Contact */}
                      <TableCell />

                      {/* GST */}
                      <TableCell />

                      {/* Follow Date */}
                      <TableCell />

                      {/* Remarks */}
                      <TableCell />

                      {/* Actions — sticky right:0 */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          right: 0,
                          zIndex: 22,
                          bgcolor: `${T.successBg} !important`,
                        }}
                      />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── Edit Modal ── */}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* ── Dialog Header ── */}
        <Box
          sx={{
            background: "#6862e0",
            px: 2.5,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: T.white,
                fontSize: "0.88rem",
                letterSpacing: "0.01em",
              }}
            >
              Edit Bill — Select LR
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpenEditModal(false)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              p: 0.5,
              ...sq,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.15)",
                color: T.white,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1,
            bgcolor: T.white,
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "nowrap",
            minHeight: 36,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: T.textMuted,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Net Payable:
          </Typography>
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 800,
              color: T.primary,
              whiteSpace: "nowrap",
              ml: -1,
            }}
          >
            {formatCurrency(totalNetPayable)}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.68rem",
              color: T.textMuted,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Selected:
          </Typography>
          <Chip
            label={`${freightOptions.filter((r) => r.is_bill).length} LRs`}
            size="small"
            sx={{
              ...sq,
              height: 16,
              bgcolor: T.successBg,
              color: T.success,
              border: `1px solid ${T.successBorder}`,
              fontSize: "0.58rem",
              fontWeight: 700,
              ml: -0.5,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>

        {/* ── Scrollable Table ── */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            py: 1.5,
            bgcolor: T.white,
            "&::-webkit-scrollbar": { width: 5, height: 5 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(79,70,229,0.2)",
              borderRadius: 3,
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...sq,
              border: `1.5px solid ${T.border}`,
              overflow: "hidden",
              boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            }}
          >
            <TableContainer>
              <Table
                stickyHeader
                size="small"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.72rem",
                    borderBottom: `1px solid #ccc`,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    fontSize: "0.72rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                    height: 28,
                  },
                  "& td": { height: 36, py: 0.5 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 4,
                        bgcolor: `${T.headerBg} !important`,
                        width: 36,
                      }}
                    />
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 36,
                        zIndex: 4,
                        bgcolor: `${T.headerBg} !important`,
                        width: 32,
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell>LR No</TableCell>
                    <TableCell>Party Name</TableCell>
                    <TableCell>Truck No</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Loading Date</TableCell>
                    <TableCell>Net Payable</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {freightOptions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="center"
                        sx={{ py: 5, border: "none", color: T.textSub }}
                      >
                        No freight records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    freightOptions.map((row, idx) => {
                      const isEven = idx % 2 === 0;
                      const rowBg = row.is_bill
                        ? T.successBg
                        : isEven
                          ? T.baseRow
                          : T.altRow;
                      const stickyBg = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 1,
                      };

                      return (
                        <TableRow
                          key={row.freight_id}
                          hover
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* Checkbox sticky */}
                          <TableCell
                            padding="checkbox"
                            sx={{
                              position: "sticky",
                              left: 0,
                              ...stickyBg,
                              p: "2px 4px",
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={row.is_bill || false}
                              onChange={() =>
                                handleIsBillToggle(row.freight_id)
                              }
                              sx={{
                                p: 0,
                                color: "rgba(79,70,229,0.4)",
                                "&.Mui-checked": { color: T.success },
                              }}
                            />
                          </TableCell>

                          {/* S.No sticky */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 36,
                              ...stickyBg,
                              fontWeight: 700,
                              color: T.textMuted,
                            }}
                          >
                            {idx + 1}
                          </TableCell>

                          {/* LR Number */}
                          <TableCell>
                            <Chip
                              label={row.lr_number || `#${row.freight_id}`}
                              size="small"
                              sx={{
                                ...sq,
                                height: 16,
                                bgcolor: T.primaryBg,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.5 },
                              }}
                            />
                          </TableCell>

                          {/* Party Name */}
                          <TableCell>
                            <Tooltip title={row.party_name || "—"} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 500,
                                  maxWidth: 130,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row.party_name || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* Truck No */}
                          <TableCell>
                            <Chip
                              label={row.truck_no || "—"}
                              size="small"
                              sx={{
                                ...sq,
                                height: 16,
                                bgcolor: "rgba(255,152,0,0.08)",
                                color: "#e65100",
                                border: `1px solid rgba(255,152,0,0.25)`,
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.5 },
                              }}
                            />
                          </TableCell>

                          {/* From */}
                          <TableCell sx={{ textAlign: "left" }}>
                            {row.from_location || "—"}
                          </TableCell>

                          {/* To */}
                          <TableCell sx={{ textAlign: "left" }}>
                            {row.to_location || "—"}
                          </TableCell>

                          {/* Loading Date */}
                          <TableCell>{formatDate(row.loading_date)}</TableCell>

                          {/* Net Payable */}
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                color: T.primary,
                              }}
                            >
                              ₹{row.net_payable ?? 0}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* ── Footer Actions ── */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 2,
            py: 1.25,
            bgcolor: T.white,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setOpenEditModal(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSubmit}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Save Changes
          </Button>
        </Box>
      </Dialog>

      {/* ── Popovers ── */}
      <ConfirmPopover
        anchorEl={settleAnchorEl}
        onClose={() => {
          setSettleAnchorEl(null);
          setSettleId(null);
        }}
        onConfirm={() => handleSettle(settleId)}
        title="Settle this Bill?"
        message="Mark this bill as fully settled?"
        isSettle
      />
      <ConfirmPopover
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setDeleteId(null);
        }}
        onConfirm={() => handleDelete(deleteId)}
        title="Delete this Bill?"
        message="This action cannot be undone."
      />
    </Box>
  );
}
